/**
 * SoleadLab 2.0 - Advanced Rule Modal
 *
 * Create/edit modal for site-level advanced alarm rules.
 * Supports 3 form variants: zero_check, deviation, expression.
 */

import { useState, useMemo, useRef } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { useMonitoringStore, useUIStore } from '@/stores';
import type {
  AdvancedAlarmRule,
  AdvancedRuleType,
  AlarmSeverity,
  AlarmOperator,
  RuleExclusion,
  ZeroCheckConfig,
  DeviationConfig,
  ExpressionConfig,
  ExpressionVariable,
  ExpressionCondition,
} from '@/types/monitoring';
import './AdvancedRuleModal.css';

const OPERATORS: { value: AlarmOperator; label: string }[] = [
  { value: '>', label: '>' },
  { value: '>=', label: '>=' },
  { value: '<', label: '<' },
  { value: '<=', label: '<=' },
  { value: '==', label: '==' },
  { value: '!=', label: '!=' },
];

export default function AdvancedRuleModal() {
  const modalData = useUIStore((s) => s.modalData);
  const closeModal = useUIStore((s) => s.closeModal);
  const addNotification = useUIStore((s) => s.addNotification);
  const schema = useMonitoringStore((s) => s.schema);
  const projectConfig = useMonitoringStore((s) => s.projectConfig);
  const addAdvancedRule = useMonitoringStore((s) => s.addAdvancedRule);
  const updateAdvancedRule = useMonitoringStore((s) => s.updateAdvancedRule);

  const mode = (modalData?.mode as string) ?? 'create';
  const ruleId = modalData?.ruleId as string | undefined;
  const initialType = (modalData?.ruleType as AdvancedRuleType) ?? 'zero_check';

  const existingRule = ruleId
    ? (projectConfig?.advanced_rules ?? []).find((r) => r.id === ruleId)
    : null;

  // ── Form State ──
  const [name, setName] = useState(existingRule?.name ?? '');
  const [ruleType] = useState<AdvancedRuleType>(existingRule?.type ?? initialType);
  const [severity, setSeverity] = useState<AlarmSeverity>(existingRule?.severity ?? 'warning');
  const [message, setMessage] = useState(existingRule?.message ?? '');

  // Zero check state
  const [zcShowSuggestions, setZcShowSuggestions] = useState(false);
  const zcInputRef = useRef<HTMLDivElement>(null);
  const [zcPattern, setZcPattern] = useState(existingRule?.zero_check?.metric_pattern ?? '');
  const [zcEntityType, setZcEntityType] = useState(existingRule?.zero_check?.entity_type ?? '');
  const [zcOp, setZcOp] = useState<AlarmOperator>(existingRule?.zero_check?.op ?? '==');
  const [zcValue, setZcValue] = useState(String(existingRule?.zero_check?.value ?? 0));
  const [zcMatchMode, setZcMatchMode] = useState<'any' | 'all'>(existingRule?.zero_check?.match_mode ?? 'any');

  // Deviation state
  const [devEntityType, setDevEntityType] = useState(existingRule?.deviation?.entity_type ?? '');
  const [devMetric, setDevMetric] = useState(existingRule?.deviation?.metric ?? '');
  const [devThreshold, setDevThreshold] = useState(String(existingRule?.deviation?.threshold_pct ?? 10));
  const [devReference, setDevReference] = useState<'average' | 'max'>(existingRule?.deviation?.reference ?? 'average');
  const [devMinValue, setDevMinValue] = useState(existingRule?.deviation?.min_value !== undefined ? String(existingRule.deviation.min_value) : '');

  // Expression state
  const [exprVars, setExprVars] = useState<ExpressionVariable[]>(
    existingRule?.expression?.variables ?? [{ name: 'v1', entity_id: '', metric: '' }]
  );
  const [exprConds, setExprConds] = useState<ExpressionCondition[]>(
    existingRule?.expression?.conditions ?? [{ left: 'v1', op: '==', right: '0' }]
  );
  const [exprCombinator, setExprCombinator] = useState<'AND' | 'OR'>(
    existingRule?.expression?.combinator ?? 'AND'
  );

  // Exclusions state
  const [exclusions, setExclusions] = useState<RuleExclusion[]>(
    existingRule?.exclusions ?? []
  );

  // ── Schema-derived options ──
  const entityTypes = useMemo(() => {
    if (!schema) return [];
    const types = new Set(schema.entities.map((e) => e.entity_type));
    return Array.from(types).sort();
  }, [schema]);

  const metricsForEntityType = useMemo(() => {
    if (!schema) return [];
    const type = ruleType === 'deviation' ? devEntityType : '';
    if (!type) return [];
    const entities = schema.entities.filter((e) => e.entity_type === type);
    const metricSet = new Map<string, string>();
    for (const entity of entities) {
      for (const m of entity.metrics) {
        if (!metricSet.has(m.key)) {
          metricSet.set(m.key, m.label || m.key);
        }
      }
    }
    return Array.from(metricSet.entries()).map(([key, label]) => ({ key, label }));
  }, [schema, devEntityType, ruleType]);

  // Metrics available for a given entity (for exclusion dropdowns)
  const metricsForEntity = (entityId: string) => {
    if (!schema) return [];
    const ent = schema.entities.find((e) => e.entity_id === entityId);
    return ent?.metrics.map((m) => ({ key: m.key, label: m.label || m.key })) ?? [];
  };

  const allMetricPrefixes = useMemo(() => {
    if (!schema) return [];
    const prefixes = new Set<string>();
    for (const entity of schema.entities) {
      for (const m of entity.metrics) {
        const parts = m.key.split('_');
        if (parts.length >= 2) {
          prefixes.add(parts.slice(0, 2).join('_'));
        }
        prefixes.add(m.key);
      }
    }
    return Array.from(prefixes).sort();
  }, [schema]);

  const filteredPrefixes = useMemo(() => {
    if (!zcPattern) return allMetricPrefixes;
    const lower = zcPattern.toLowerCase();
    return allMetricPrefixes.filter((p) => p.toLowerCase().includes(lower));
  }, [allMetricPrefixes, zcPattern]);

  // ── Save handler ──
  const handleSave = async () => {
    if (!name.trim()) return;

    const id = existingRule?.id ?? crypto.randomUUID();

    let zero_check: ZeroCheckConfig | undefined;
    let deviation: DeviationConfig | undefined;
    let expression: ExpressionConfig | undefined;

    if (ruleType === 'zero_check') {
      if (!zcPattern.trim()) return;
      zero_check = {
        metric_pattern: zcPattern.trim(),
        entity_type: zcEntityType || undefined,
        op: zcOp,
        value: parseFloat(zcValue) || 0,
        match_mode: zcMatchMode,
      };
    } else if (ruleType === 'deviation') {
      if (!devEntityType || !devMetric) return;
      deviation = {
        entity_type: devEntityType,
        metric: devMetric,
        threshold_pct: parseFloat(devThreshold) || 10,
        reference: devReference,
        min_value: devMinValue ? parseFloat(devMinValue) : undefined,
      };
    } else if (ruleType === 'expression') {
      const validVars = exprVars.filter((v) => v.entity_id && v.metric);
      const validConds = exprConds.filter((c) => c.left && c.right);
      if (validVars.length === 0 || validConds.length === 0) return;
      expression = {
        variables: validVars,
        conditions: validConds,
        combinator: exprCombinator,
      };
    }

    // Filter out incomplete exclusions
    const validExclusions = exclusions.filter((e) => e.entity_id && e.metric);

    const rule: AdvancedAlarmRule = {
      id,
      name: name.trim(),
      type: ruleType,
      enabled: existingRule?.enabled ?? true,
      severity,
      message: message.trim() || undefined,
      exclusions: validExclusions.length > 0 ? validExclusions : [],
      zero_check,
      deviation,
      expression,
    };

    // B3a — persist via a canonical write (returns true only on a confirmed save). Keep the
    // modal open on failure so the user can retry; the conflict/error message comes from the store.
    const ok = mode === 'edit' && existingRule
      ? await updateAdvancedRule(id, rule)
      : await addAdvancedRule(rule);
    if (ok) {
      closeModal();
    } else {
      addNotification({
        type: 'error',
        title: mode === 'edit' && existingRule ? 'Could not save rule' : 'Could not add rule',
        message: useMonitoringStore.getState().configError ?? 'Please retry.',
      });
    }
  };

  // ── Expression helpers ──
  const addVariable = () => {
    const nextName = `v${exprVars.length + 1}`;
    setExprVars([...exprVars, { name: nextName, entity_id: '', metric: '' }]);
  };

  const updateVariable = (index: number, field: keyof ExpressionVariable, value: string) => {
    setExprVars(exprVars.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
  };

  const removeVariable = (index: number) => {
    if (exprVars.length <= 1) return;
    setExprVars(exprVars.filter((_, i) => i !== index));
  };

  const addCondition = () => {
    setExprConds([...exprConds, { left: '', op: '==', right: '0' }]);
  };

  const updateCondition = (index: number, field: keyof ExpressionCondition, value: string) => {
    setExprConds(exprConds.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const removeCondition = (index: number) => {
    if (exprConds.length <= 1) return;
    setExprConds(exprConds.filter((_, i) => i !== index));
  };

  // ── Exclusion helpers ──
  const addExclusion = () => {
    setExclusions([...exclusions, { entity_id: '', metric: '' }]);
  };

  const updateExclusion = (index: number, field: keyof RuleExclusion, value: string) => {
    setExclusions(exclusions.map((e, i) => {
      if (i !== index) return e;
      if (field === 'entity_id') return { entity_id: value, metric: '' };
      return { ...e, [field]: value };
    }));
  };

  const removeExclusion = (index: number) => {
    setExclusions(exclusions.filter((_, i) => i !== index));
  };

  // ── Preview text ──
  const previewText = useMemo(() => {
    if (ruleType === 'zero_check') {
      return `if ${zcPattern}* ${zcOp} ${zcValue} (${zcMatchMode}) → ${severity.toUpperCase()}`;
    }
    if (ruleType === 'deviation') {
      return `if ${devMetric || '?'} deviation > ${devThreshold}% from ${devReference} across ${devEntityType || '?'} → ${severity.toUpperCase()}`;
    }
    if (ruleType === 'expression') {
      const conds = exprConds
        .map((c) => `${c.left} ${c.op} ${c.right}`)
        .join(` ${exprCombinator} `);
      return `if ${conds} → ${severity.toUpperCase()}`;
    }
    return '';
  }, [ruleType, zcPattern, zcOp, zcValue, zcMatchMode, devMetric, devThreshold, devReference, devEntityType, exprConds, exprCombinator, severity]);

  const typeLabel = ruleType === 'zero_check' ? 'Zero / Threshold Check'
    : ruleType === 'deviation' ? 'Deviation Detection'
    : 'Custom Expression';

  return (
    <Modal
      isOpen={true}
      onClose={closeModal}
      title={`${mode === 'edit' ? 'Edit' : 'New'} Advanced Rule — ${typeLabel}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={closeModal}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            {mode === 'edit' ? 'Update' : 'Create'} Rule
          </Button>
        </>
      }
    >
      <div className="arm-form">
        {/* Rule Name */}
        <div className="arm-row">
          <label className="arm-label">Rule Name</label>
          <input
            className="arm-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., DC Current Zero Check"
            maxLength={120}
          />
        </div>

        {/* Severity */}
        <div className="arm-row">
          <label className="arm-label">Severity</label>
          <div className="arm-severity">
            <button
              className={`arm-severity__pill arm-severity__pill--warning ${severity === 'warning' ? 'arm-severity__pill--active' : ''}`}
              onClick={() => setSeverity('warning')}
            >
              WARNING
            </button>
            <button
              className={`arm-severity__pill arm-severity__pill--critical ${severity === 'critical' ? 'arm-severity__pill--active' : ''}`}
              onClick={() => setSeverity('critical')}
            >
              CRITICAL
            </button>
          </div>
        </div>

        <div className="arm-divider" />

        {/* ═══ Zero Check Form ═══ */}
        {ruleType === 'zero_check' && (
          <>
            <div className="arm-row" ref={zcInputRef}>
              <label className="arm-label">Metric Prefix</label>
              <div className="arm-combo">
                <input
                  className="arm-input"
                  value={zcPattern}
                  onChange={(e) => { setZcPattern(e.target.value); setZcShowSuggestions(true); }}
                  onFocus={() => setZcShowSuggestions(true)}
                  onBlur={() => { setTimeout(() => setZcShowSuggestions(false), 150); }}
                  placeholder="e.g., I_DC — type to filter"
                />
                {zcShowSuggestions && filteredPrefixes.length > 0 && (
                  <div className="arm-combo__list">
                    {filteredPrefixes.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`arm-combo__item ${p === zcPattern ? 'arm-combo__item--active' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); setZcPattern(p); setZcShowSuggestions(false); }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="arm-hint">All metrics starting with this prefix will be checked</span>
            </div>

            <div className="arm-row">
              <label className="arm-label">Entity Type (optional)</label>
              <select
                className="arm-select"
                value={zcEntityType}
                onChange={(e) => setZcEntityType(e.target.value)}
              >
                <option value="">All entities</option>
                {entityTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="arm-row--inline">
              <label className="arm-label">Condition</label>
              <select className="arm-select" value={zcOp} onChange={(e) => setZcOp(e.target.value as AlarmOperator)}>
                {OPERATORS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                className="arm-input arm-input--sm"
                type="number"
                value={zcValue}
                onChange={(e) => setZcValue(e.target.value)}
              />
            </div>

            <div className="arm-row">
              <label className="arm-label">Match Mode</label>
              <div className="arm-radios">
                <label className="arm-radio">
                  <input type="radio" checked={zcMatchMode === 'any'} onChange={() => setZcMatchMode('any')} />
                  Any metric matches
                </label>
                <label className="arm-radio">
                  <input type="radio" checked={zcMatchMode === 'all'} onChange={() => setZcMatchMode('all')} />
                  All metrics match
                </label>
              </div>
            </div>
          </>
        )}

        {/* ═══ Deviation Form ═══ */}
        {ruleType === 'deviation' && (
          <>
            <div className="arm-row">
              <label className="arm-label">Entity Type</label>
              <select
                className="arm-select"
                value={devEntityType}
                onChange={(e) => setDevEntityType(e.target.value)}
              >
                <option value="">Select...</option>
                {entityTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="arm-row">
              <label className="arm-label">Metric</label>
              <select
                className="arm-select"
                value={devMetric}
                onChange={(e) => setDevMetric(e.target.value)}
              >
                <option value="">Select...</option>
                {metricsForEntityType.length > 0 && (metricsForEntityType as Array<{key: string; label: string}>).map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="arm-row--inline">
              <label className="arm-label">Threshold</label>
              <input
                className="arm-input arm-input--sm"
                type="number"
                value={devThreshold}
                onChange={(e) => setDevThreshold(e.target.value)}
                min="0"
                max="100"
              />
              <span className="arm-hint">%</span>
            </div>

            <div className="arm-row">
              <label className="arm-label">Reference</label>
              <div className="arm-radios">
                <label className="arm-radio">
                  <input type="radio" checked={devReference === 'average'} onChange={() => setDevReference('average')} />
                  Average
                </label>
                <label className="arm-radio">
                  <input type="radio" checked={devReference === 'max'} onChange={() => setDevReference('max')} />
                  Maximum
                </label>
              </div>
            </div>

            <div className="arm-row">
              <label className="arm-label">Min Value (optional)</label>
              <input
                className="arm-input arm-input--md"
                type="number"
                value={devMinValue}
                onChange={(e) => setDevMinValue(e.target.value)}
                placeholder="Ignore below"
              />
              <span className="arm-hint">Entities with values below this are excluded (avoids night-time false positives)</span>
            </div>
          </>
        )}

        {/* ═══ Expression Form ═══ */}
        {ruleType === 'expression' && (
          <>
            <div className="arm-row">
              <label className="arm-label">Variables</label>
              <div className="arm-vars">
                {exprVars.map((v, i) => (
                  <div key={i} className="arm-var-row">
                    <span className="arm-var-name">{v.name}</span>
                    <span className="arm-var-eq">=</span>
                    <select
                      className="arm-select"
                      value={v.entity_id}
                      onChange={(e) => updateVariable(i, 'entity_id', e.target.value)}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <option value="">Entity...</option>
                      {schema?.entities.map((ent) => (
                        <option key={ent.entity_id} value={ent.entity_id}>
                          {ent.entity_id} ({ent.entity_type})
                        </option>
                      ))}
                    </select>
                    <select
                      className="arm-select"
                      value={v.metric}
                      onChange={(e) => updateVariable(i, 'metric', e.target.value)}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <option value="">Metric...</option>
                      {v.entity_id && schema?.entities
                        .find((e) => e.entity_id === v.entity_id)
                        ?.metrics.map((m) => (
                          <option key={m.key} value={m.key}>{m.label || m.key}</option>
                        ))}
                    </select>
                    {exprVars.length > 1 && (
                      <button className="arm-var-remove" onClick={() => removeVariable(i)} title="Remove">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button className="arm-add-btn" onClick={addVariable}>
                  + Add Variable
                </button>
              </div>
            </div>

            <div className="arm-row">
              <label className="arm-label">Combinator</label>
              <div className="arm-radios">
                <label className="arm-radio">
                  <input type="radio" checked={exprCombinator === 'AND'} onChange={() => setExprCombinator('AND')} />
                  AND (all must match)
                </label>
                <label className="arm-radio">
                  <input type="radio" checked={exprCombinator === 'OR'} onChange={() => setExprCombinator('OR')} />
                  OR (any can match)
                </label>
              </div>
            </div>

            <div className="arm-row">
              <label className="arm-label">Conditions</label>
              <div className="arm-conds">
                {exprConds.map((c, i) => (
                  <div key={i} className="arm-cond-row">
                    {i > 0 && <span className="arm-cond-combinator">{exprCombinator}</span>}
                    {i === 0 && <span className="arm-cond-combinator">if</span>}
                    <select
                      className="arm-select"
                      value={c.left}
                      onChange={(e) => updateCondition(i, 'left', e.target.value)}
                      style={{ width: 70 }}
                    >
                      <option value="">...</option>
                      {exprVars.map((v) => (
                        <option key={v.name} value={v.name}>{v.name}</option>
                      ))}
                    </select>
                    <select
                      className="arm-select"
                      value={c.op}
                      onChange={(e) => updateCondition(i, 'op', e.target.value)}
                      style={{ width: 60 }}
                    >
                      {OPERATORS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <input
                      className="arm-input"
                      value={c.right}
                      onChange={(e) => updateCondition(i, 'right', e.target.value)}
                      placeholder="0 or v2 * 0.9"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    {exprConds.length > 1 && (
                      <button className="arm-var-remove" onClick={() => removeCondition(i)} title="Remove">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button className="arm-add-btn" onClick={addCondition}>
                  + Add Condition
                </button>
              </div>
            </div>
          </>
        )}

        <div className="arm-divider" />

        {/* ═══ Exclusions ═══ */}
        <div className="arm-row">
          <label className="arm-label">
            Exclusions{exclusions.length > 0 ? ` (${exclusions.length})` : ''}
          </label>
          {exclusions.length > 0 ? (
            <div className="arm-exclusions">
              {exclusions.map((ex, i) => (
                <div key={i} className="arm-excl-row">
                  <select
                    className="arm-select"
                    value={ex.entity_id}
                    onChange={(e) => updateExclusion(i, 'entity_id', e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <option value="">Entity...</option>
                    {schema?.entities.map((ent) => (
                      <option key={ent.entity_id} value={ent.entity_id}>
                        {ent.entity_id} ({ent.entity_type})
                      </option>
                    ))}
                  </select>
                  <select
                    className="arm-select"
                    value={ex.metric}
                    onChange={(e) => updateExclusion(i, 'metric', e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <option value="">Metric...</option>
                    {metricsForEntity(ex.entity_id).map((m) => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                  <button className="arm-var-remove" onClick={() => removeExclusion(i)} title="Remove">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className="arm-hint">No exclusions — rule applies to all matching entities</span>
          )}
          <button className="arm-add-btn" onClick={addExclusion}>
            + Add Exclusion
          </button>
        </div>

        <div className="arm-divider" />

        {/* Message */}
        <div className="arm-row">
          <label className="arm-label">Message (optional)</label>
          <input
            className="arm-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Custom alarm message"
            maxLength={200}
          />
        </div>

        {/* Preview */}
        <div className="arm-row">
          <label className="arm-label">Preview</label>
          <div className="arm-preview">{previewText}</div>
        </div>
      </div>
    </Modal>
  );
}
